import { useState } from 'react';
import { CookieConsent, COOKIE_CATEGORIES } from '@/types/cookies';
import { hasAnalyticsConsent as readAnalyticsConsent } from '@/utils/consentUtils';

export type ConsentStatus = 'pending' | 'configured';

const CONSENT_KEY = 'cookie-consent';
const ACCEPTED_CONSENT_EXPIRY_MONTHS = 12;
const REJECTED_CONSENT_EXPIRY_MONTHS = 3;

// Helper function to read consent data directly from localStorage
const getConsentDataFromStorage = (): CookieConsent | null => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      const storedData = JSON.parse(stored);

      // Determine expiry based on consent type
      const consentType = storedData.consentType || 'custom';
      const expiryMonths = consentType === 'reject-all' ? REJECTED_CONSENT_EXPIRY_MONTHS : ACCEPTED_CONSENT_EXPIRY_MONTHS;
      const expiryDate = new Date(storedData.timestamp);
      expiryDate.setMonth(expiryDate.getMonth() + expiryMonths);

      if (new Date() < expiryDate) {
        // Handle migration from old formats
        if (!storedData.categories) {
          const isAccepted = storedData.status === 'accepted';
          return {
            status: 'configured',
            timestamp: storedData.timestamp,
            consentType: isAccepted ? 'accept-all' : 'reject-all',
            categories: Object.keys(COOKIE_CATEGORIES).reduce((acc, key) => {
              acc[key] = COOKIE_CATEGORIES[key].essential || isAccepted;
              return acc;
            }, {} as Record<string, boolean>)
          };
        }

        // Ensure all categories exist and add consentType if missing
        const categories = { ...storedData.categories };
        Object.keys(COOKIE_CATEGORIES).forEach(key => {
          if (categories[key] === undefined) {
            categories[key] = COOKIE_CATEGORIES[key].essential;
          }
        });

        return {
          ...storedData,
          categories,
          consentType: storedData.consentType || 'custom'
        };
      } else {
        localStorage.removeItem(CONSENT_KEY);
      }
    }
  } catch (error) {
    console.log('❌ Error parsing stored data:', error);
    localStorage.removeItem(CONSENT_KEY);
  }

  return null;
};

// Get initial consent data for React state (only for UI purposes)
const getInitialConsentData = (): CookieConsent => {
  const stored = getConsentDataFromStorage();

  if (stored) {
    return stored;
  }

  return {
    status: 'pending',
    timestamp: Date.now(),
    consentType: 'custom',
    categories: Object.keys(COOKIE_CATEGORIES).reduce((acc, key) => {
      acc[key] = COOKIE_CATEGORIES[key].essential;
      return acc;
    }, {} as Record<string, boolean>)
  };
};

export function useCookieConsent() {
  const [consentData, setConsentData] = useState<CookieConsent>(getInitialConsentData);
  const [showPreferences, setShowPreferences] = useState(false);


  const acceptAllCookies = () => {
    const categories = Object.keys(COOKIE_CATEGORIES).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {} as Record<string, boolean>);

    const consent: CookieConsent = {
      status: 'configured',
      timestamp: Date.now(),
      consentType: 'accept-all',
      categories
    };

    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setConsentData(consent);
    setShowPreferences(false);
  };

  const rejectAllCookies = () => {
    const categories = Object.keys(COOKIE_CATEGORIES).reduce((acc, key) => {
      acc[key] = COOKIE_CATEGORIES[key].essential;
      return acc;
    }, {} as Record<string, boolean>);

    const consent: CookieConsent = {
      status: 'configured',
      timestamp: Date.now(),
      consentType: 'reject-all',
      categories
    };

    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setConsentData(consent);
    setShowPreferences(false);
  };

  const updateCategoryConsent = (categoryId: string, enabled: boolean) => {
    const currentData = getConsentDataFromStorage() || consentData;
    const consent: CookieConsent = {
      ...currentData,
      status: 'configured',
      timestamp: Date.now(),
      consentType: 'custom',
      categories: {
        ...currentData.categories,
        [categoryId]: enabled
      }
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setConsentData(consent);
  };

  const saveCustomConsent = (categories: Record<string, boolean>) => {
    const consent: CookieConsent = {
      status: 'configured',
      timestamp: Date.now(),
      consentType: 'custom',
      categories
    };

    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setConsentData(consent);
    setShowPreferences(false);
  };

  const openPreferences = () => {
    setShowPreferences(true);
  };

  const closePreferences = () => {
    setShowPreferences(false);
  };

  // Always read from localStorage as source of truth
  const hasAnalyticsConsent = () => {
    return readAnalyticsConsent();
  };

  const isPending = () => {
    const currentData = getConsentDataFromStorage();
    return !currentData || currentData.status === 'pending';
  };

  const shouldShowModal = () => {
    return isPending() || showPreferences;
  };

  return {
    consentData,
    acceptAllCookies,
    rejectAllCookies,
    updateCategoryConsent,
    saveCustomConsent,
    hasAnalyticsConsent,
    isPending,
    shouldShowModal,
    showPreferences,
    openPreferences,
    closePreferences
  };
}